import React, { useState } from 'react';
import type { Message } from '@/types';
import { formatBulletPoints } from '@/lib/messageFormatter';
import { getGroundedTextSegments } from '@/utils/groundingUtils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface MessageWithCitationsProps {
  message: Message;
}

export function MessageWithCitations({ message }: MessageWithCitationsProps) {
  // If no grounding metadata, use regular formatting
  if (!message.groundingMetadata) {
    return <>{formatBulletPoints(message.content)}</>;
  }

  const segments = getGroundedTextSegments(message.content, message.groundingMetadata);
  
  // Build text with citation markers
  let textWithMarkers = '';
  const citations: Array<{ number: number; sources: any[] }> = [];
  let citationNumber = 0;
  
  segments.forEach(segment => {
    textWithMarkers += segment.content;
    if (segment.type === 'citation') {
      citationNumber++;
      textWithMarkers += `[CITATION_${citationNumber}]`;
      citations.push({
        number: citationNumber,
        sources: segment.sources || []
      });
    }
  });
  
  // Format the text with markers
  const formattedContent = formatBulletPoints(textWithMarkers);
  
  return (
    <TooltipProvider>
      <ContentWithCitations 
        formattedContent={formattedContent}
        citations={citations}
      />
    </TooltipProvider>
  );
}

interface ContentWithCitationsProps {
  formattedContent: React.ReactNode;
  citations: Array<{ number: number; sources: any[] }>;
}

function ContentWithCitations({ formattedContent, citations }: ContentWithCitationsProps) {
  // Replace citation markers with actual badge components
  return <div>{replaceCitationMarkers(formattedContent, citations)}</div>;
}

// Recursively replace citation markers in React nodes
function replaceCitationMarkers(
  node: React.ReactNode,
  citations: Array<{ number: number; sources: any[] }>
): React.ReactNode {
  if (typeof node === 'string') {
    // Check if this string contains citation markers
    const citationRegex = /\[CITATION_(\d+)\]/g;
    if (!citationRegex.test(node)) {
      return node;
    }
    
    // Split by citation markers and rebuild with badges
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const matches = node.matchAll(/\[CITATION_(\d+)\]/g);
    
    for (const match of matches) {
      const citationNum = parseInt(match[1]);
      const citation = citations.find(c => c.number === citationNum);
      
      // Add text before marker
      if (match.index! > lastIndex) {
        parts.push(node.substring(lastIndex, match.index));
      }
      
      // Add citation badge
      if (citation) {
        parts.push(
          <CitationBadge
            key={`citation-${citationNum}`}
            number={citation.number}
            sources={citation.sources}
          />
        );
      }
      
      lastIndex = match.index! + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < node.length) {
      parts.push(node.substring(lastIndex));
    }
    
    return <>{parts}</>;
  }

  if (React.isValidElement(node)) {
    const element = node as React.ReactElement<any>;
    const children = React.Children.toArray(element.props.children);
    const newChildren = children.map((child, index) => 
      <React.Fragment key={index}>{replaceCitationMarkers(child, citations)}</React.Fragment>
    );
    
    return React.cloneElement(element, element.props, newChildren);
  }

  if (Array.isArray(node)) {
    return node.map((child, index) => 
      <React.Fragment key={index}>{replaceCitationMarkers(child, citations)}</React.Fragment>
    );
  }

  return node;
}

interface CitationBadgeProps {
  number: number;
  sources: Array<{
    title: string;
    url: string;
    text?: string;
    confidence?: number;
  }>;
}

interface ConfidenceBadgeProps {
  confidence: number;
}

function ConfidenceBadge({ confidence }: ConfidenceBadgeProps) {
  const percentage = confidence * 100;
  
  // Determine color based on confidence level
  const getConfidenceColor = () => {
    if (percentage >= 80) {
      return {
        bg: 'rgb(24, 128, 56)',
        text: 'white'
      };
    } else if (percentage >= 50) {
      return {
        bg: 'rgb(234, 179, 8)', // yellow
        text: 'white'
      };
    } else {
      return {
        bg: 'rgb(220, 38, 38)', // red
        text: 'white'
      };
    }
  };
  
  const colors = getConfidenceColor();
  
  return (
    <div 
      className="inline-flex items-center px-2 py-1 rounded text-xs font-medium mt-2"
      style={{ 
        backgroundColor: colors.bg,
        color: colors.text
      }}
    >
      Confidence: {percentage.toFixed(1)}%
    </div>
  );
}

function CitationBadge({ number, sources }: CitationBadgeProps) {
  const [isOpen, setIsOpen] = useState(false);

  const getDisplayName = (title: string, url: string) => {
    const generic = !title || title.toLowerCase() === 'document' || title.toLowerCase() === 'source' || title.toLowerCase() === 'web source';
    if (!generic) {
      return title.replace(/\.json$/i, '.pdf');
    }
    try {
      const cleanUrl = url.split('?')[0].split('#')[0];
      const parts = cleanUrl.split('/');
      const name = decodeURIComponent(parts[parts.length - 1] || 'Document');
      return name.replace(/\.json$/i, '.pdf');
    } catch {
      return (title || 'Document').replace(/\.json$/i, '.pdf');
    }
  };

  return (
    <Tooltip open={isOpen} onOpenChange={setIsOpen} delayDuration={0}>
      <TooltipTrigger asChild>
        <span 
          className="citation-badge cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            setIsOpen(!isOpen);
          }}
        >
          {number}
        </span>
      </TooltipTrigger>
      <TooltipContent 
        className="google-studio-tooltip p-0" 
        side="top" 
        align="start"
        onPointerDownOutside={() => setIsOpen(false)}
        onEscapeKeyDown={() => setIsOpen(false)}
      >
        <div className="w-full max-w-[90vw]">
          {/* Sticky header */}
          <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-popover">
            <div className="font-semibold text-sm text-gray-700 dark:text-gray-200">
              Source {number}
            </div>
          </div>
          
          {/* Scrollable content area */}
          <div className="max-h-[500px] overflow-y-auto overflow-x-hidden px-3 py-2">
            {sources.map((source, idx) => (
              <div 
                key={idx} 
                className="mb-4 last:mb-0 min-w-0"
              >
                <div className="text-sm font-medium mb-2 break-words">
                  {getDisplayName(source.title, source.url)}
                </div>
                
                {/* Chunk text - independently scrollable */}
                {source.text && (
                  <div 
                    className="max-h-[200px] overflow-auto bg-gray-50 dark:bg-gray-800 rounded p-3 border border-gray-200 dark:border-gray-700"
                  >
                    <pre className="text-xs text-gray-700 dark:text-gray-300 m-0 p-0 whitespace-pre font-sans block w-full">
                      {source.text}
                    </pre>
                  </div>
                )}
                
                {/* Confidence score */}
                {source.confidence !== undefined && (
                  <ConfidenceBadge confidence={source.confidence} />
                )}
                
                {/* Divider between sources */}
                {idx < sources.length - 1 && (
                  <div className="border-t border-gray-200 dark:border-gray-700 mt-4" />
                )}
              </div>
            ))}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
